import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import PrimaryButton from '../Button/PrimaryButton';
import { Box, CircularProgress, Divider, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import strings from '../../common/Translation/Translate';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { DIALOG_TONES, tonedActionButtonSx, tonedDialogPaperSx } from '../modal/dialogTones';

type DialogTone = 'error';

type DialogProps = {
  open: boolean;
  handleClose: () => void;
  children: any;
  title: any;
  contentText: string;
  loading: boolean;
  closeButton?: boolean;
  showButton?: boolean;
  fullWidth?: string;
  maxWidth?: any;
  handleSave: () => void;
  saveButtonText?: string;
  disabledCondition?: any;
  tone?: DialogTone;
  headerIcon?: React.ReactNode;
};

export const FormDialog: React.FC<DialogProps> = ({
  open,
  handleClose,
  children,
  title,
  contentText,
  closeButton = false,
  showButton = true,
  loading,
  handleSave,
  maxWidth = 'sm',
  saveButtonText,
  disabledCondition = false,
  tone,
  headerIcon,
}) => {
  const theme = useTheme();
  const screenSizeUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const toneColors = tone ? DIALOG_TONES[tone] : null;
  const headerBg = toneColors ? toneColors.surface : theme.palette.secondary.main;
  const headerColor = toneColors ? '#2F2F2F' : theme.palette.secondary.contrastText;
  return (
    <div>
      <Dialog
        maxWidth={maxWidth}
        open={open}
        onClose={handleClose}
        sx={{
          '.MuiDialog-paper': {
            width: screenSizeUpMd ? '80%' : '90%',
            [theme.breakpoints.down('sm')]: { maxHeight: '100%', m: 0 },
          },
          ...(toneColors ? tonedDialogPaperSx(toneColors) : {}),
        }}>
        <Grid
          sx={{
            backgroundColor: headerBg,
            color: headerColor,
            borderBottom: toneColors ? `1px solid ${toneColors.border}` : undefined,
          }}
          container
          item
          alignItems="center"
          p={2}>
          <Grid item xs={10}>
            {title && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {headerIcon && toneColors ? (
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: toneColors.iconBg,
                      color: toneColors.accent,
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}>
                    {headerIcon}
                  </Box>
                ) : (
                  headerIcon
                )}
                <Typography fontWeight="600" fontSize="18px" letterSpacing="-0.01em">
                  {title}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={2} textAlign={'right'}>
            {closeButton && (
              <IconButton size="small" color="default" className="deleteButton" onClick={handleClose} aria-label="edit">
                <HighlightOffOutlinedIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
        {!toneColors && (
          <Grid item>
            <Divider />
          </Grid>
        )}
        <DialogContent sx={{ marginX: '3px' }}>
          <Grid container flexDirection={'column'} justifyContent="center" gap={2}>
            <Typography
              sx={
                toneColors
                  ? { fontSize: 14, color: '#6A6A6A', lineHeight: 1.55 }
                  : { fontSize: 13 }
              }>
              {contentText}
            </Typography>
            {children}
          </Grid>
        </DialogContent>
        {showButton && (
          <DialogActions sx={{ p: 2 }}>
            <PrimaryButton
              variant="outlined"
              onClick={handleClose}
              sx={
                toneColors
                  ? {
                      borderColor: '#D6D6D6',
                      color: '#5A5A5A',
                      backgroundColor: '#fff',
                      boxShadow: 'none',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#C0C0C0',
                        backgroundColor: '#FAFAFA',
                      },
                    }
                  : undefined
              }>
              {strings.common_button_back}
            </PrimaryButton>
            <PrimaryButton
              disabled={loading || disabledCondition}
              variant="contained"
              onClick={handleSave}
              sx={toneColors ? tonedActionButtonSx(toneColors) : undefined}>
              {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
              {saveButtonText || strings.common_button_save}
            </PrimaryButton>
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
};
